import type { PointerEvent as ReactPointerEvent } from 'react';

export interface DragCallbacks {
  /** Called for every pointermove after the 4px slop threshold. */
  move?: (ev: PointerEvent) => void;
  /** Called on pointerup/cancel; `moved` is false for a tap. */
  up?: (ev: PointerEvent, moved: boolean) => void;
}

/**
 * Pointer-capture drag helper. Call from a React pointerdown handler; it
 * captures the pointer on the event's currentTarget and drives the callbacks
 * with native events until release.
 */
export function startDrag(e: ReactPointerEvent, cb: DragCallbacks): void {
  const el = e.currentTarget as HTMLElement;
  const id = e.pointerId;
  try {
    el.setPointerCapture(id);
  } catch {
    // capture can fail if the pointer is already gone — drag just no-ops
  }
  const sx = e.clientX;
  const sy = e.clientY;
  let moved = false;
  const onMove = (ev: PointerEvent) => {
    if (ev.pointerId !== id) return;
    if (!moved && Math.abs(ev.clientX - sx) + Math.abs(ev.clientY - sy) > 4) moved = true;
    if (moved) cb.move?.(ev);
  };
  const done = (ev: PointerEvent) => {
    if (ev.pointerId !== id) return;
    el.removeEventListener('pointermove', onMove);
    el.removeEventListener('pointerup', done);
    el.removeEventListener('pointercancel', done);
    cb.up?.(ev, moved);
  };
  el.addEventListener('pointermove', onMove);
  el.addEventListener('pointerup', done);
  el.addEventListener('pointercancel', done);
}

/** Double-tap detector usable for mouse and touch alike (no native dblclick
 * on iOS Safari). Returns true when a tap lands within 350ms / 30px of the
 * previous one. */
export function makeTapDetector(): (clientX: number, clientY: number) => boolean {
  let t = 0;
  let x = 0;
  let y = 0;
  return (clientX, clientY) => {
    const now = Date.now();
    const dbl = now - t < 350 && Math.abs(clientX - x) < 30 && Math.abs(clientY - y) < 30;
    t = dbl ? 0 : now;
    x = clientX;
    y = clientY;
    return dbl;
  };
}
